import { useEffect, useState } from 'react';
import { BookOpen, Gear } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  useSidebar,
} from '@/components/ui-kit/sidebar';
import { APP_PATHS } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useToast } from '@/hooks/use-toast';
import { matchesSidebarPath } from '@/lib/utils/sidebar-utils';
// The file itself, not the module index: the index pulls in the attendance pages, which import core.
import { AttendanceStatusLink } from '@/modules/time-attendance/attendance/components/attendance-status-link/attendance-status-link';
import {
  LogoSection,
  SidebarMenuGroupComponent,
  SidebarMenuItemComponent,
} from '@/components/core';
import { usePermittedSidebar } from '@/hooks/use-permitted-sidebar';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui-kit/button';

const FOOTER_ICON_BUTTON = 'size-8 shrink-0 rounded-md text-muted-foreground hover:text-foreground';

/**
 * The sidebar footer (HR Module Prototype, 2 Nov): today's attendance status on the left (it links
 * to Attendance), then the HR Handbook (not built yet, "Soon") and the Settings gear. The gear shows
 * for whoever may open at least one settings page and stays highlighted on every settings page. In
 * the collapsed rail the three stack.
 */
const SidebarFooterBar = ({
  isRail,
  showSettings,
  onItemClick,
}: {
  isRail: boolean;
  showSettings: boolean;
  onItemClick?: () => void;
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { toast } = useToast();
  const canSeeAttendance = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_ATTENDANCE);
  const isSettingsActive = matchesSidebarPath(pathname, APP_PATHS.SETTINGS);
  const handbookLabel = `${t('HR_HANDBOOK')} · ${t('SOON')}`;

  return (
    <SidebarFooter
      className={cn(
        'flex gap-0.5 border-t border-sidebar-border p-2',
        isRail ? 'flex-col items-center px-1' : 'flex-row items-center'
      )}
    >
      {canSeeAttendance && <AttendanceStatusLink compact={isRail} onClick={onItemClick} />}
      {!isRail && <span className="flex-1" />}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={FOOTER_ICON_BUTTON}
        aria-label={handbookLabel}
        title={handbookLabel}
        onClick={() => toast({ title: handbookLabel, description: t('SOON_PAGE_DESCRIPTION') })}
      >
        <BookOpen className="size-[18px]" />
      </Button>
      {showSettings && (
        <Button
          asChild
          variant="ghost"
          size="icon"
          className={cn(
            FOOTER_ICON_BUTTON,
            isSettingsActive && 'bg-sidebar-accent text-foreground hover:bg-sidebar-accent'
          )}
        >
          <Link
            to={APP_PATHS.SETTINGS}
            onClick={onItemClick}
            aria-label={t('SETTINGS')}
            title={t('SETTINGS')}
            aria-current={isSettingsActive ? 'page' : undefined}
          >
            <Gear className="size-[18px]" weight={isSettingsActive ? 'fill' : 'regular'} />
          </Link>
        </Button>
      )}
    </SidebarFooter>
  );
};

/**
 * AppSidebar — the HR navigation as the prototype's Component Map draws it: a floating panel, 16rem wide or
 * a 5rem rail with each label under its icon. The top bar's sidebar button collapses and expands it.
 *
 * Groups (Attendance, Leave Management, ...) form a single accordion: opening one
 * closes the rest, and the group holding the active route auto-opens on navigation —
 * mirroring the Angular app's FuseNavigationService behavior for the same menu.
 */
export const AppSidebar = () => {
  const { pathname } = useLocation();
  const { setOpenMobile, open, isMobile, state } = useSidebar();
  const { sections, settingsSections } = usePermittedSidebar();
  const [openGroupTitle, setOpenGroupTitle] = useState<string | null>(null);

  useEffect(() => {
    if (!isMobile) {
      setOpenMobile(false);
    }
  }, [pathname, setOpenMobile, isMobile]);

  useEffect(() => {
    const activeSection = sections.find((section) =>
      section.items.some((item) => matchesSidebarPath(pathname, item.path))
    );
    setOpenGroupTitle(activeSection?.title ?? null);
  }, [pathname, sections]);

  // Below 1024px the sidebar is a drawer: picking a page closes it.
  const onItemClick = isMobile ? () => setOpenMobile(false) : undefined;
  const isRail = !isMobile && state === 'collapsed';

  return (
    // Floating panel (8px from the window edge, rounded, bordered): 16rem, or the 5rem rail.
    <Sidebar variant="floating" collapsible="icon">
      <SidebarHeader
        className={cn(
          'flex shrink-0 flex-row items-center gap-2 p-0',
          isMobile ? 'h-14 border-b border-sidebar-border px-3' : 'h-14 pl-3 pr-2',
          isRail && 'justify-center pl-0 pr-0'
        )}
      >
        <LogoSection open={open} isMobile={isMobile} onClose={() => setOpenMobile(false)} />
      </SidebarHeader>

      <SidebarContent
        id="app-sidebar"
        className={cn(
          'flex flex-col gap-0 overflow-x-hidden pb-2 pt-1 text-sm',
          isRail ? 'px-1' : 'px-2'
        )}
      >
        <SidebarMenu className={isRail ? 'gap-1' : 'gap-px'}>
          {sections.map((section) =>
            section.icon ? (
              <SidebarMenuGroupComponent
                key={section.title}
                title={section.title}
                icon={section.icon}
                items={section.items}
                isRail={isRail}
                isOpen={openGroupTitle === section.title}
                onToggle={() =>
                  setOpenGroupTitle((prev) => (prev === section.title ? null : section.title))
                }
                onItemClick={onItemClick}
              />
            ) : (
              <SidebarMenuItemComponent
                key={section.items[0].id}
                item={section.items[0]}
                isRail={isRail}
                onClick={onItemClick}
              />
            )
          )}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooterBar
        isRail={isRail}
        showSettings={settingsSections.length > 0}
        onItemClick={onItemClick}
      />
    </Sidebar>
  );
};
